import { useState } from 'react';
import {
  Button,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNavigate } from 'react-router-dom';
import { ConfirmSelectionButton, SelectionPageHeader } from '../../../common/components';
import type { Product } from '../../../../types/product';
import type { ActivityFilters } from '../../../activity/types';

interface MultipleProductSelectionHeaderProps {
  companyId: number;
  showFilters: boolean;
  onToggleFilters: () => void;
  selectedProductsMap: Map<number, Product>;
  hasActiveFilters: boolean;
  returnPath?: string;
  activityFilters?: ActivityFilters;
  infoText?: string;
  onSelectAll?: () => Promise<void> | void;
}

export const MultipleProductSelectionHeader = ({
  companyId,
  showFilters,
  onToggleFilters,
  selectedProductsMap,
  hasActiveFilters,
  returnPath,
  activityFilters,
  infoText,
  onSelectAll,
}: MultipleProductSelectionHeaderProps) => {
  const navigate = useNavigate();
  const [selectingAll, setSelectingAll] = useState(false);

  const handleSelectAll = async () => {
    if (!onSelectAll) return;

    setSelectingAll(true);
    try {
      await onSelectAll();
    } finally {
      setSelectingAll(false);
    }
  };

  const handleConfirmMultiSelection = () => {
    if (selectedProductsMap.size > 0 && returnPath) {
      const selectedProducts = Array.from(selectedProductsMap.values());
      
      // Оновлюємо activityFilters з новими продуктами
      const updatedFilters: ActivityFilters = {
        ...activityFilters,
        products: selectedProducts,
      } as ActivityFilters;

      navigate(returnPath, {
        state: {
          activityFilters: updatedFilters,
        },
      });
    }
  };

  const selectedProductIdsCount = selectedProductsMap.size;

  return (
    <>
      <SelectionPageHeader
        title="Вибір продукту"
        companyId={companyId}
        returnPath={returnPath}
      >
        <Tooltip title="Показати/сховати фільтри">
          <IconButton
            size="small"
            onClick={onToggleFilters}
            color={showFilters || hasActiveFilters ? 'primary' : 'default'}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>

        {onSelectAll && (
          <Button
            color="inherit"
            startIcon={<DoneAllIcon />}
            onClick={handleSelectAll}
            disabled={selectingAll}
          >
            Вибрати всі
          </Button>
        )}

        <ConfirmSelectionButton
          onClick={handleConfirmMultiSelection}
          count={selectedProductIdsCount}
          disabled={selectedProductIdsCount === 0}
        />
      </SelectionPageHeader>

      <Alert severity="info" sx={{ mb: 2 }}>
        {infoText ?? 'Виберіть продукти для фільтрації активностей (до 5 шт.)'}
      </Alert>
    </>
  );
};
